import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
  haalUitVeld,
  hangLagen,
  inspringBlok,
  kleurDoelen,
  schoneHtml,
  kopVoetVan,
  springIn,
  verwijderKopVoet,
  voegKopVoetToe,
  zetAchtergrond,
  type KopVoetSoort,
  type Lagen,
} from "./rawPrintLagen";

// communicatie — de bewerkbare documentweergave voor afdruksjablonen met vaste opmaak ("db-doc").
//
// WAAROM DIT BESTAAT. Deze sjablonen (afstandsverklaring, pensionovereenkomst, …) dragen hun eigen
// afdruk-CSS en een exacte tabelindeling. Door TipTap halen betekent: opmaak platgeslagen en het
// document stuk. De React-editor loste dat op door ze in een <textarea> met ruwe HTML te zetten — veilig,
// maar daarmee verdween het bewerken zelf: wie een veld wil toevoegen moet nu tussen <td>'s zoeken.
//
// Jmix deed het anders, en beter: `comm-editor.js#_refreshRawDoc` toont het document in een iframe met de
// afdrukstijl erin, zet `contenteditable` op de body, onthoudt waar de cursor stond en laat de knoppen
// buiten het kader er `execCommand`-bewerkingen en veldinvoegingen op los. Je bewerkt dus wat je ziet,
// en de markup eromheen blijft letterlijk staan. Dit is die aanpak, geport.
//
// De herhaalblokken (`data-repeat`) krijgen een stippelkader met "Wordt herhaald voor elk(e) …". Dat zit
// alleen in de editor-CSS, niet in de afdrukstijl — de pdf toont dat kader dus niet.

/** Wat de editor eromheen met het document kan doen. */
export interface RawPrintHandle {
  /** Voegt HTML in op de laatst bekende cursorpositie (of achteraan als die er niet is). */
  insertHtml: (html: string) => void;
  /** Voert een `document.execCommand` uit op het document — vet, cursief, uitlijning. */
  exec: (command: string, value?: string) => void;
  /** De huidige inhoud van het document. */
  currentHtml: () => string;
  /** Achtergrondkleur van de cellen in de selectie (of het blok van de cursor); null haalt hem weg. */
  achtergrond: (kleur: string | null) => void;
  /** Het blok van de cursor één tabstop in- (1) of terug (-1) laten springen. */
  inspringen: (richting: 1 | -1) => void;
  /** Zet een kop- of voettekstblok in het document (of neemt het bestaande) en zet de cursor erin. */
  kopVoet: (soort: KopVoetSoort) => void;
  /** Haalt een kop- of voettekstblok weg. */
  verwijderKopVoet: (soort: KopVoetSoort) => void;
  /** Welke kop- en voettekstblokken het document nu heeft. */
  kopVoetAanwezig: () => Record<KopVoetSoort, boolean>;
  /** De HTML van wat nu geselecteerd is, of null als er niets (of niets zichtbaars) geselecteerd is. */
  selectieHtml: () => string | null;
  /** Vervangt de selectie door deze HTML. Doet niets als er niets geselecteerd is. */
  vervangSelectie: (html: string) => void;
}

const EDITOR_CSS = `
/* Het bureau met de vellen erop, zoals in een tekstverwerker. De body is het papier: precies A4
   breed met de marges van de afdrukstijl (@page margin 2cm), dus een regel breekt hier waar hij in
   de PDF ook breekt. De vellen zelf tekent rawPrintLagen eronder; de body is doorzichtig. Zijn hoogte
   komt ook daarvandaan: een heel aantal vellen. */
html { background: #e4e7eb; }
body { position: relative; margin: calc(14px + var(--ce-extra-boven, 0px)) auto 14px; width: 210mm;
  box-sizing: border-box; padding: 20mm; padding-top: var(--ce-boven, 20mm);
  background: transparent; min-height: var(--ce-body-hoogte, 297mm); }
/* Een logo-token als bron is in de editor nog geen plaatje. */
img[src^="{"] { content: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='60'%3E%3Crect x='1' y='1' width='118' height='58' rx='6' fill='%23f7f8fa' stroke='%23b8c0c8' stroke-dasharray='4 3'/%3E%3Ctext x='60' y='35' font-family='Helvetica,Arial' font-size='12' text-anchor='middle' fill='%237a8490'%3Elogo vestiging%3C/text%3E%3C/svg%3E"); width: 66pt; height: auto; }
body:focus { outline: none; }
[data-repeat] {
  position: relative;
  border: 1.5px dashed #1f9d57;
  border-radius: 7px;
  padding: 20px 8px 8px;
  margin: 10px 0;
}
[data-repeat]::before {
  content: "\\21bb  Wordt herhaald voor elk(e) " attr(data-repeat);
  position: absolute; top: 0; left: 0; right: 0;
  background: #e7f6ee; color: #1f9d57;
  font: 600 10px/1.7 Helvetica, Arial, sans-serif;
  letter-spacing: .02em;
  padding: 1px 8px;
  border-bottom: 1px dashed #1f9d57;
  border-radius: 6px 6px 0 0;
}
/* Een herhaalde tabelrij (of tbody): een kader met een kop erboven breekt de tabel, dus hier alleen
   een groene tint met een lijn boven en onder. Welke lijst hij herhaalt staat in de blokknoppen. */
tr[data-repeat], tbody[data-repeat] { border: 0; padding: 0; margin: 0; border-radius: 0; }
tr[data-repeat]::before, tbody[data-repeat]::before { content: none; }
tr[data-repeat] > td, tr[data-repeat] > th, tbody[data-repeat] > tr > td {
  background-color: #eef8f2;
  box-shadow: inset 0 1px 0 #1f9d57, inset 0 -1px 0 #1f9d57;
}
/* De voettekst staat bovenaan in de sjabloonbron. Dat kan, want de afdrukstijl haalt hem met
   position:fixed uit de tekststroom en zet hem in de ondermarge van elke pagina. Een browser
   heeft geen paginas: daar plakt hij aan de onderrand van het kijkvenster en schuift hij dwars
   door de tekst mee. Hier staat hij in de ondermarge van het laatste vel (de plek rekent
   rawPrintLagen uit); de andere vellen krijgen een kopie. De afdrukstijl blijft ongemoeid; de pdf
   verandert niet. */
.db-footer {
  position: absolute;
  top: var(--ce-voet-top, auto);
  bottom: auto;
  left: 20mm;
  right: 20mm;
}
/* De koptekst: net als de voettekst uit de tekststroom, bovenaan zijn vel (rawPrintLagen rekent de plek uit). */
.db-header {
  position: absolute;
  top: var(--ce-kop-top, 10mm);
  bottom: auto;
  left: 20mm;
  right: 20mm;
}
.db-header-eerste { top: var(--ce-kop-eerste-top, 10mm); }
/* Kop- en voettekst zijn vakken, zoals in Word: een stippellijn met een label erboven. Alleen hier in de
   editor; niets hiervan komt in het sjabloon of op papier. De kopieën op de andere vellen staan buiten de
   body en krijgen dit niet. */
body .db-header, body .db-footer { outline: 1px dashed #9aa4ae; outline-offset: 3px; min-height: 12px; }
body .db-header::before, body .db-footer::before {
  position: absolute; top: -18px; left: -3px; padding: 0 5px; border-radius: 3px;
  font: 600 9px/1.5 Helvetica, Arial, sans-serif; color: #5b6570; background: #eef1f4;
  pointer-events: none; white-space: nowrap;
}
body .db-header::before { content: "Koptekst \\2014  elke pagina"; }
body .db-header-eerste::before { content: "Koptekst \\2014  pagina 1"; }
body .db-footer::before { content: "Voettekst \\2014  elke pagina"; }
`;

export const RawPrintCanvas = forwardRef<RawPrintHandle, {
  /** De HTML van het sjabloon. Wordt één keer geladen; daarna is het iframe de bron. */
  bodyHtml: string;
  /** De afdrukstylesheet van de server, zodat het er hier uitziet als op papier. */
  printCss: string;
  /** Elke toetsaanslag in het document. */
  onChange: (html: string) => void;
  /** Uit bij een gepubliceerd sjabloon dat alleen te lezen is. */
  readOnly?: boolean;
  ariaLabel: string;
  /**
   * Rechtermuisknop op een niet-lege selectie, met de muispositie in schermcoordinaten. De schil tekent
   * het menu, niet dit iframe: daarbinnen zou het wegvallen achter de rand van het vel.
   */
  onSelectieMenu?: (positie: { x: number; y: number }) => void;
}>(function RawPrintCanvas(
  { bodyHtml, printCss, onChange, readOnly = false, ariaLabel, onSelectieMenu },
  ref,
) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const rangeRef = useRef<Range | null>(null);
  // De laatst bekende inhoud, ook als het document even niet te bereiken is (een iframe dat nog laadt,
  // of een omgeving zonder execCommand). Zo landt een ingevoegd veld altijd ergens in plaats van
  // nergens — achteraan is niet ideaal, verdwijnen is erger.
  const htmlRef = useRef<string>(bodyHtml ?? "");
  // De pagina's en de blokknoppen boven het document; zie rawPrintLagen.
  const lagenRef = useRef<Lagen | null>(null);
  // De body gaat één keer in het iframe. Zou hij bij elke wijziging opnieuw geladen worden, dan springt
  // de cursor na elke aanslag naar het begin.
  const bouwSrcDoc = (html: string) =>
    [
      "<!doctype html><html><head><meta charset=\"utf-8\"><base target=\"_blank\">",
      // Een sjabloon is data, geen programma: scripts en externe bronnen blijven geweerd. Dit
      // vervangt de sandbox, die het document ook onbewerkbaar maakte.
      "<meta http-equiv=\"Content-Security-Policy\" content=\"script-src 'none'; object-src 'none'\">",
      "<style>",
      printCss,
      EDITOR_CSS,
      "</style></head><body>",
      html || "",
      "</body></html>",
    ].join("");

  const [srcDoc, setSrcDoc] = useState(() => bouwSrcDoc(bodyHtml));

  // Komt er van buiten een ander document binnen (een overgenomen brief, een ander sjabloon), dan
  // gaat dat opnieuw het iframe in. Alleen bij een echt andere inhoud: opnieuw laden bij elke
  // toetsaanslag zou de cursor telkens naar het begin gooien.
  useEffect(() => {
    if (bodyHtml === htmlRef.current) return;
    htmlRef.current = bodyHtml;
    setSrcDoc(bouwSrcDoc(bodyHtml));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyHtml]);

  const doc = () => frameRef.current?.contentDocument ?? null;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const wire = () => {
      const d = frame.contentDocument;
      if (!d?.body) return;
      d.body.setAttribute("contenteditable", readOnly ? "false" : "true");

      // Onthoud waar de cursor stond: de knoppen zitten búiten het iframe, dus zonder dit belandt een
      // ingevoegd veld altijd achteraan in plaats van waar de gebruiker klikte.
      const remember = () => {
        const sel = d.getSelection();
        if (sel && sel.rangeCount && d.body.contains(sel.getRangeAt(0).commonAncestorContainer)) {
          rangeRef.current = sel.getRangeAt(0).cloneRange();
        }
      };
      d.addEventListener("selectionchange", remember);
      d.body.addEventListener("contextmenu", (e) => {
        remember();
        const bereik = rangeRef.current;
        const iets = bereik && !bereik.collapsed && bereik.toString().trim().length > 0;
        if (!iets || !onSelectieMenu) return;
        e.preventDefault();
        // De muispositie zit in de coordinaten van het iframe; de schil tekent het menu erbuiten en
        // moet daarom de offset van het iframe erbij optellen.
        const muis = e as MouseEvent;
        const kader = frame.getBoundingClientRect();
        onSelectieMenu({ x: kader.left + muis.clientX, y: kader.top + muis.clientY });
      });
      d.body.addEventListener("keyup", remember);
      d.body.addEventListener("mouseup", remember);
      d.body.addEventListener("input", () => {
        remember();
        // Zonder de veldblokjes van de editor: zie schoneHtml.
        const html = schoneHtml(d.body);
        htmlRef.current = html;
        onChange(html);
      });

      lagenRef.current?.weg();
      lagenRef.current = hangLagen(d, {
        alleenLezen: readOnly,
        bijWijziging: (inhoud) => {
          htmlRef.current = inhoud;
          onChange(inhoud);
        },
      });
    };

    // srcdoc laadt asynchroon; bij een cache-hit kan het document er al staan.
    frame.addEventListener("load", wire);
    if (frame.contentDocument?.readyState === "complete") wire();
    return () => {
      frame.removeEventListener("load", wire);
      lagenRef.current?.weg();
      lagenRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, onSelectieMenu]);

  const focusAtRange = (d: Document): Selection | null => {
    frameRef.current?.contentWindow?.focus();
    d.body.focus();
    const sel = d.getSelection();
    if (!sel) return null;
    const saved = rangeRef.current;
    if (saved && d.body.contains(saved.commonAncestorContainer)) {
      sel.removeAllRanges();
      sel.addRange(saved);
    } else if (!sel.rangeCount) {
      const r = d.createRange();
      r.selectNodeContents(d.body);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    // Nooit binnen een veld invoegen: dan wordt {Dier.Naam} iets als {Dier{Relatie.Naam}.Naam}.
    haalUitVeld(d);
    return sel;
  };

  useImperativeHandle(ref, () => ({
    insertHtml(html: string) {
      if (readOnly) return;
      const d = doc();
      if (!d?.body) {
        htmlRef.current = (htmlRef.current ?? "") + html;
        onChange(htmlRef.current);
        return;
      }
      focusAtRange(d);
      try {
        d.execCommand("insertHTML", false, html);
      } catch {
        // Oude browsers zonder insertHTML: dan achteraan, liever dat dan niets.
        d.body.insertAdjacentHTML("beforeend", html);
      }
      htmlRef.current = schoneHtml(d.body);
      onChange(htmlRef.current);
      lagenRef.current?.herbereken();
    },
    selectieHtml() {
      const d = doc();
      const bereik = rangeRef.current;
      if (!d?.body || !bereik || !d.body.contains(bereik.commonAncestorContainer) || bereik.collapsed) {
        return null;
      }
      const houder = d.createElement("div");
      houder.appendChild(bereik.cloneContents());
      // Lege selectie (alleen witruimte) telt niet: daar valt niets aan te herschrijven.
      return houder.textContent?.trim() ? houder.innerHTML : null;
    },
    vervangSelectie(html: string) {
      const d = doc();
      const bereik = rangeRef.current;
      if (!d?.body || readOnly || !bereik || !d.body.contains(bereik.commonAncestorContainer)) return;
      focusAtRange(d);
      try {
        // insertHTML vervángt een niet-lege selectie; dat is precies wat hier moet gebeuren.
        d.execCommand("insertHTML", false, html);
      } catch {
        return;
      }
      htmlRef.current = schoneHtml(d.body);
      onChange(htmlRef.current);
      lagenRef.current?.herbereken();
    },
    exec(command: string, value?: string) {
      const d = doc();
      if (!d?.body || readOnly) return;
      focusAtRange(d);
      try {
        d.execCommand(command, false, value ?? "");
      } catch {
        return;
      }
      htmlRef.current = schoneHtml(d.body);
      onChange(htmlRef.current);
      lagenRef.current?.herbereken();
    },
    currentHtml() {
      const d = doc();
      return d?.body ? schoneHtml(d.body) : htmlRef.current ?? "";
    },
    achtergrond(kleur: string | null) {
      const d = doc();
      const bereik = rangeRef.current;
      if (!d?.body || readOnly || !bereik || !d.body.contains(bereik.commonAncestorContainer)) return;
      const doelen = kleurDoelen(bereik, d.body);
      if (!doelen.length) return;
      lagenRef.current?.bewerk(() => zetAchtergrond(doelen, kleur));
      focusAtRange(d);
    },
    inspringen(richting: 1 | -1) {
      const d = doc();
      const bereik = rangeRef.current;
      if (!d?.body || readOnly || !bereik || !d.body.contains(bereik.commonAncestorContainer)) return;
      const blok = inspringBlok(bereik, d.body);
      if (!blok) return;
      lagenRef.current?.bewerk(() => springIn(blok, richting));
      focusAtRange(d);
    },
    kopVoet(soort: KopVoetSoort) {
      const d = doc();
      if (!d?.body || readOnly) return;
      const gevonden: { blok: HTMLElement | null } = { blok: null };
      if (lagenRef.current) {
        lagenRef.current.bewerk(() => {
          gevonden.blok = voegKopVoetToe(d.body, soort);
        });
      } else {
        gevonden.blok = voegKopVoetToe(d.body, soort);
        htmlRef.current = schoneHtml(d.body);
        onChange(htmlRef.current);
      }
      const blok = gevonden.blok;
      if (!blok) return;
      // De cursor aan het eind van het blok, zodat je meteen kunt typen, en het blok in beeld.
      const r = d.createRange();
      r.selectNodeContents(blok);
      r.collapse(false);
      rangeRef.current = r;
      focusAtRange(d);
      blok.scrollIntoView?.({ block: "center" });
    },
    verwijderKopVoet(soort: KopVoetSoort) {
      const d = doc();
      if (!d?.body || readOnly) return;
      if (lagenRef.current) {
        lagenRef.current.bewerk(() => verwijderKopVoet(d.body, soort));
      } else {
        verwijderKopVoet(d.body, soort);
        htmlRef.current = schoneHtml(d.body);
        onChange(htmlRef.current);
      }
    },
    kopVoetAanwezig() {
      const d = doc();
      const heeft = (soort: KopVoetSoort) => !!d?.body && !!kopVoetVan(d.body, soort);
      return { kop: heeft("kop"), kopEerste: heeft("kopEerste"), voet: heeft("voet") };
    },
  }));

  return (
    <iframe
      ref={frameRef}
      className="comm-rawprint__frame"
      title={ariaLabel}
      srcDoc={srcDoc}
      // Geen sandbox: die maakt het document onbewerkbaar. Scripts zijn geweerd met een CSP in het
      // document zelf (zie srcDoc hierboven), zoals de bron het ook doet.
    />
  );
});
